import { IconX } from '@tabler/icons-react';
import React from 'react';
import { createPortal } from 'react-dom';
import { formatCount } from '@utils/currency';

interface OpeningStockCalculationModalProps {
    isOpen: boolean;
    onClose: () => void;
    skuCode: string; // e.g. "FG-001"
    productName: string; // e.g. "Premium Arabica Coffee 500g"
    weekLabel: string; // "Week 2"

    // Data for calculation (Week N-1)
    prevWeekLabel: string; // "Week 1"
    prevOpening: number;
    prevInTransit: number;
    prevConsumption: number;

    // Result (Week N)
    currentOpening: number;
    isInitialWeek?: boolean;
}

const OpeningStockCalculationModal: React.FC<
    OpeningStockCalculationModalProps
> = ({
    isOpen,
    onClose,
    skuCode,
    productName,
    weekLabel,
    prevWeekLabel,
    prevOpening,
    prevInTransit,
    prevConsumption,
    currentOpening,
    isInitialWeek,
}) => {
    if (!isOpen) return null;

    // Helper to format numbers to integer (least integer)
    // Using Math.floor() as "least integer" often implies the integer part or lower bound in inventory contexts
    const formatNumber = (num: number) =>
        formatCount(Math.floor(num), { maxDecimals: 0 });

    // Use portal to render outside the table stacking context
    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200">
                {/* Header */}
                <div className="flex items-start justify-between border-b border-slate-100 p-6 pb-4">
                    <div>
                        <h3 className="text-lg font-bold text-slate-900">
                            Opening Stock Calculation - {weekLabel}
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">
                            {skuCode} – {productName}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="cursor-pointer rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                    >
                        <IconX className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="space-y-4 p-6">
                    {/* If Initial Week, show simplified view */}
                    {isInitialWeek ? (
                        <>
                            <div className="rounded-xl bg-blue-50 px-5 py-6 text-center">
                                <p className="mb-2 text-sm font-semibold text-slate-900">
                                    Initial Opening Stock
                                </p>
                                <span className="text-3xl font-bold text-slate-900">
                                    {formatNumber(currentOpening)}{' '}
                                    <span className="text-base font-normal text-slate-500">
                                        units
                                    </span>
                                </span>
                                <p className="mt-2 text-xs text-slate-500">
                                    This is the starting inventory for the
                                    planning period.
                                </p>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Previous Week Opening Stock (Blue) */}
                            <div className="flex items-center justify-between rounded-xl bg-blue-50 px-5 py-4">
                                <div>
                                    <p className="text-sm font-semibold text-slate-900">
                                        Previous Week Opening Stock
                                    </p>
                                    <p className="text-xs text-slate-500">
                                        {prevWeekLabel}
                                    </p>
                                </div>
                                <span className="text-lg font-bold text-slate-900">
                                    {formatNumber(prevOpening)} units
                                </span>
                            </div>

                            {/* Plus Icon */}
                            <div className="flex justify-center text-green-500">
                                <svg
                                    className="h-5 w-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 4.5v15m7.5-7.5h-15"
                                    />
                                </svg>
                            </div>

                            {/* In-Transit Stock (Green) */}
                            <div className="flex items-center justify-between rounded-xl bg-green-50 px-5 py-4">
                                <div>
                                    <p className="text-sm font-semibold text-slate-900">
                                        In-Transit Stock
                                    </p>
                                    <p className="text-xs text-slate-500">
                                        Arriving in {prevWeekLabel}
                                    </p>
                                </div>
                                <span className="text-lg font-bold text-green-700">
                                    {formatNumber(prevInTransit)} units
                                </span>
                            </div>

                            {/* Minus Icon */}
                            <div className="flex justify-center text-orange-500">
                                <svg
                                    className="h-5 w-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M19.5 12h-15"
                                    />
                                </svg>
                            </div>

                            {/* Consumption (Orange) */}
                            <div className="flex items-center justify-between rounded-xl bg-orange-50 px-5 py-4">
                                <div>
                                    <p className="text-sm font-semibold text-slate-900">
                                        Consumption (Forecast)
                                    </p>
                                    <p className="text-xs text-slate-500">
                                        Demand in {prevWeekLabel}
                                    </p>
                                </div>
                                <span className="text-lg font-bold text-orange-700">
                                    {formatNumber(prevConsumption)} units
                                </span>
                            </div>

                            {/* Equals Icon */}
                            <div className="flex justify-center text-purple-500">
                                <svg
                                    className="h-5 w-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M3.75 9h16.5m-16.5 6.75h16.5"
                                    />
                                </svg>
                            </div>

                            {/* Opening Stock (Purple) */}
                            <div className="flex items-center justify-between rounded-xl bg-purple-50 px-5 py-4">
                                <div>
                                    <p className="text-sm font-semibold text-slate-900">
                                        Opening Stock
                                    </p>
                                    <p className="text-xs text-slate-500">
                                        {weekLabel}
                                    </p>
                                </div>
                                <span className="text-lg font-bold text-purple-700">
                                    {formatNumber(currentOpening)} units
                                </span>
                            </div>

                            {/* Calculation Footer */}
                            <div className="mt-2 rounded-lg bg-slate-50 px-4 py-3 font-mono text-xs text-slate-600">
                                Calculation: {formatNumber(prevOpening)} +{' '}
                                {formatNumber(prevInTransit)} -{' '}
                                {formatNumber(prevConsumption)} ={' '}
                                {formatNumber(currentOpening)}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default OpeningStockCalculationModal;
